import { type BlogCard_portfolioProject$key } from '#app/__generated__/relay/BlogCard_portfolioProject.graphql.js';
import { type BlogCard_userProfile$key } from '#app/__generated__/relay/BlogCard_userProfile.graphql.js';
import { BlogCategoriesList } from '#app/components/BlogCategoriesList/BlogCategoriesList.js';
import { Flex } from '#app/components/Layout/components/Flex/Flex.js';
import { Menu } from '#app/components/Menu/Menu.js';
import { Text } from '#app/components/Primitives/Text.js';
import { ProjectCoverWrapper } from '#app/components/ProjectCover/ProjectCoverWrapper.js';
import { type Sortable } from '#app/components/SortableListView/types.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useDeletePortfolioProject } from '#app/hooks/useDeletePortfolioProject.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { useOnElementViewed } from '#app/hooks/useOnElementViewed.js';
import * as ProjectPreview from './BlogCard.styles.js';
import { CloseIcon, EditIcon, MoreIcon, MoveIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { useCallback, useState } from 'react';
import { css, styled } from 'styled-components';

type BlogCardProps = Sortable & {
  readonly canEdit: boolean;
  readonly disabled?: boolean;
  readonly isGuideProject?: boolean;
  readonly portfolioProject: BlogCard_portfolioProject$key;
  readonly position?: number;
  readonly userProfileRef?: BlogCard_userProfile$key;
};

const CategoriesDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CategoryRow = styled(Flex.Row)`
  ${({ theme }) => css`
    gap: 8px;
    margin: 16px 0 0;

    ${theme.mediaQueries.lg} {
      margin-top: auto;
    }
  `}
`;

export const BlogCard = ({
  canEdit,
  disabled,
  isDragging,
  isGuideProject,
  portfolioProject,
  position,
  sortableListeners,
  userProfileRef,
}: BlogCardProps) => {
  const data = useFragment<BlogCard_portfolioProject$key>(
    graphql`
      fragment BlogCard_portfolioProject on PortfolioProject {
        id
        analyticsId
        author {
          id
        }
        blogCategories {
          edges {
            node {
              id
              name
            }
          }
        }
        slug
        title
        description
        cover {
          ...ProjectCoverWrapper_portfolioProjectCover
          ... on PortfolioProjectImageCover {
            __typename
            image {
              id
              animated
              height
              originalFormat
              sizeBytes
              uid
              width
            }
          }
          ... on PortfolioProjectTextCover {
            __typename
            style
          }
        }
        contentImages {
          edges {
            node {
              id
              animated
              height
              originalFormat
              sizeBytes
              uid
              width
            }
          }
        }
      }
    `,
    portfolioProject,
  );

  const user = useFragment<BlogCard_userProfile$key>(
    graphql`
      fragment BlogCard_userProfile on UserProfile {
        id
        analyticsId
        visitorCanEdit
      }
    `,
    userProfileRef ?? null,
  );

  const blogCategories =
    data.blogCategories?.edges.map(({ node: { id: categoryId, name } }) => ({
      id: categoryId,
      name,
    })) ?? [];

  const [editMenuOpen, setEditMenuOpen] = useState(false);

  const toggleMenu = useCallback(() => {
    setEditMenuOpen((open) => !open);
  }, [setEditMenuOpen]);

  const { track } = useAnalytics();
  const navigation = useNavigation();

  const onViewedRef = useOnElementViewed<HTMLDivElement>(() => {
    if (data && user && typeof position === 'number') {
      track('portfolio_project_card', {
        action: 'viewed',
        is_owner: user.visitorCanEdit,
        owner_id: user.analyticsId,
        portfolio_project_id: data.analyticsId,
        position_index: position,
        section: 'profile',
      });
    }
  });

  const { cover, description, id, slug, title } = data;

  const [openDeletePortfolioProjectModal] = useDeletePortfolioProject({
    id,
  });

  if (!data) {
    return null;
  }

  const shouldDisplaySortHandle = canEdit && sortableListeners;

  return (
    <ProjectPreview.Card
      disabled={disabled}
      elementRef={onViewedRef}
      isDragging={isDragging}
    >
      {shouldDisplaySortHandle && (
        <ProjectPreview.CardSortHandle
          role="button"
          tabIndex={0}
          {...sortableListeners}
        >
          <MoveIcon size={20} />
        </ProjectPreview.CardSortHandle>
      )}

      {canEdit && (
        <ProjectPreview.Button>
          <Menu
            isOpen={editMenuOpen}
            placement="bottom-end"
            toggleMenu={toggleMenu}
          >
            <Menu.Reference>
              <ProjectPreview.MenuButton
                aria-label="Open project menu"
                icon={MoreIcon}
                onClick={toggleMenu}
                variant="V2TertiaryOutline"
              />
            </Menu.Reference>
            <Menu.Panel>
              <Menu.Item
                icon={EditIcon}
                onClick={() => {
                  navigation.push(
                    getRoute('/p/@slug/edit', {
                      args: {
                        slug,
                      },
                    }),
                  );
                }}
              >
                Edit Project
              </Menu.Item>

              <Menu.Item
                icon={CloseIcon}
                onClick={openDeletePortfolioProjectModal}
              >
                <Text color="alertError">Delete Project</Text>
              </Menu.Item>
            </Menu.Panel>
          </Menu>
        </ProjectPreview.Button>
      )}

      <ProjectPreview.Body>
        <ProjectPreview.Cover
          onClick={() => {
            if (position && user) {
              track('portfolio_project_card', {
                action: 'tapped',
                cta: 'image',
                is_owner: user.visitorCanEdit,
                owner_id: user.analyticsId,
                portfolio_project_id: data.analyticsId,
                position_index: position,
                section: 'profile',
              });
            }
          }}
          to={getRoute('/p/@slug', { args: { slug } })}
        >
          <ProjectCoverWrapper coverRef={cover} />
        </ProjectPreview.Cover>
        <ProjectPreview.Details addEditButtonPadding={canEdit}>
          {isGuideProject ? (
            <ProjectPreview.GuideTitle>
              Only you can see this
            </ProjectPreview.GuideTitle>
          ) : null}
          <ProjectPreview.Title
            onClick={() => {
              if (position && user) {
                track('portfolio_project_card', {
                  action: 'tapped',
                  cta: 'title',
                  is_owner: user.visitorCanEdit,
                  owner_id: user.analyticsId,
                  portfolio_project_id: data.analyticsId,
                  position_index: position,
                  section: 'profile',
                });
              }
            }}
            to={getRoute('/p/@slug', { args: { slug } })}
          >
            {title}
          </ProjectPreview.Title>

          <CategoriesDescriptionContainer>
            {description ? (
              <ProjectPreview.Description
                color="uiBlackMediumEmphasis"
                textStyle="bodySmall"
              >
                {description}
              </ProjectPreview.Description>
            ) : null}

            {blogCategories.length ? (
              <CategoryRow>
                <BlogCategoriesList
                  categories={blogCategories}
                  limit={3}
                  size="small"
                />
              </CategoryRow>
            ) : null}
          </CategoriesDescriptionContainer>
        </ProjectPreview.Details>
      </ProjectPreview.Body>
    </ProjectPreview.Card>
  );
};
